import React, { MouseEventHandler, useMemo } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { toast } from 'react-toastify';
import SahamDialog from 'components/SahamDialog';
import { translate } from 'containers/services/translate';
import messages from 'containers/CourtManagerLegalProceedings/JudgementAdd/messages';
import { formatDecision } from 'containers/CourtManagerLegalProceedings/JudgementAdd/utils';
import { PAGES } from 'routes';
import { pushTo } from 'containers/services/routes-helper';
import { PROCEDURE_STATUSES } from 'containers/data-structure';
import {
  IJudgementAdd,
  JudgementOnMeritsType,
} from 'containers/LawyerLegalProceedingsPage/typings';
import hash from 'object-hash';
import { get, isEmpty } from 'lodash';
import { Listings } from 'containers/AppContainer/typings';
import userPrivileges, {
  PRIVILEGES,
} from 'containers/services/security/user-privileges';
import { PROCEDURE_TYPES } from 'containers/LawyerLegalProceedingsPage/constants';
import currentUserStorage from 'containers/services/security/current-user-storage';
import { showAlert } from 'containers/CourtManagerLegalProceedings/JudgementAdd/Dialogs/utils';
import { ExpertReportType } from 'containers/common/LegalProceedings/ExpertReport/typings';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import { formatCourtFileNumber } from 'containers/LawyerLegalProceedingsPage/judgementAdd/utils';

export interface ValidationDialogProps {
  intl: IntlShape;
  open: boolean;
  handleClose: Function;
  requestMakeJudgementAddDecision: Function;
  lawyer: string;
  claimId: number;
  claimNumber: string;
  judgementAdd: IJudgementAdd | {};
  judgementAddHash: string;
  judgementAddDocumentsHash: string;
  listing: Listings;
  policyInfo: any;
  claim: any;
  expertReport?: ExpertReportType;
  judgementOnMerits?: JudgementOnMeritsType;
  toUpdate: boolean;
  civilParty: boolean;
  setIsButtonRedirect: Function;
  setOpenFeeNoteDialog: Function;
}

export const ValidationDialog: React.FC<ValidationDialogProps> = (
  props,
): JSX.Element => {
  const judgementAddDocumentId = get(
    get(props.judgementAdd, 'judgementAddDocuments', []).find(
      (el: any) => !el.status,
    ),
    'id',
  );
  const handleValidate = () => {
    let payload;
    props.setIsButtonRedirect(true);
    if (props.judgementAddHash === hash(props.judgementAdd)) {
      payload = formatDecision(
        PROCEDURE_STATUSES.VALIDATED,
        props.claimId,
        props.judgementAdd,
        null,
        get(props.claim, 'assignment.assignedLawyer'),
        props.toUpdate,
      );
    } else {
      const { judgementAddDocuments, ...rest } = props.judgementAdd as any;

      payload = {
        ...(props.civilParty && props.toUpdate
          ? { ...rest }
          : !props.civilParty && props.toUpdate
          ? {
              judgementAddDocuments: get(
                props.judgementAdd,
                'judgementAddDocuments',
                [],
              ).filter(
                (item: any) =>
                  props.judgementAddDocumentsHash !==
                    hash(
                      get(props.judgementAdd, 'judgementAddDocuments', []),
                    ) && isEmpty(item.status),
              ),
            }
          : { ...props.judgementAdd }),
        claimId: props.claimId,
        id: get(props.judgementAdd, 'id'),
        status: props.toUpdate
          ? get(props.judgementAdd, 'status')
          : PROCEDURE_STATUSES.MODIFIED,
      };
    }

    props.requestMakeJudgementAddDecision(
      {
        ...payload,
        judgementAdd: {
          ...payload.judgementAdd,
          courtFile: formatCourtFileNumber(payload.judgementAdd?.courtFile),
        },
        claimNumber: props.claimNumber,
        procedureType: PROCEDURE_TYPES.FIRST_INSTANCE,
        judiciaryProcessId: get(props.judgementAdd, 'judiciaryProcessId'),
        judgementAddDocumentId,
        username: currentUserStorage.get().username,
        isMonitoringCourtManager:
          userPrivileges.userHasPrivilege(
            PRIVILEGES.ISCOURTMONITORINGMANAGER,
          ) && userPrivileges.userHasPrivilege(PRIVILEGES.ISMANAGER),
        toUpdate: props.toUpdate,
        isInboxesActive: featureService.hasFeature(
          FEATURES.ATTRIBUTION_INBOXES_MANAGER,
        ),
        isTrackingActive: featureService.hasFeature(
          FEATURES.TRACKING_ACTIONS_USER,
        ),
      },
      handleSuccessCallback,
    );
  };

  const handleSuccessCallback = () => {
    props.toUpdate
      ? toast.success(
          translate(messages.courtManagerUpdateDialogSuccessMessage, props),
        )
      : toast.success(
          translate(
            messages.courtManagerValidationDialogSuccessMessage,
            props,
            {
              lawyer: props.lawyer,
            },
          ),
        );
    props.handleClose();
    featureService.hasFeature(FEATURES.EVOLUTIONS_FEE_NOTE)
      ? !props.toUpdate && props.setOpenFeeNoteDialog(true)
      : userPrivileges.userHasPrivilege(PRIVILEGES.ISCOURTMONITORINGMANAGER) &&
        userPrivileges.userHasPrivilege(PRIVILEGES.ISMANAGER)
      ? pushTo(PAGES.COURT_MANAGER_HOME)()
      : pushTo(PAGES.COURT_MANAGER_ASSIGNED_CLAIM)();
  };
  const { hasExpertise, hasJom } = useMemo(
    () =>
      showAlert(
        get(props.judgementAdd, 'judgementAddDocuments', []).find((item: any) =>
          isEmpty(item.status),
        ),
        props.expertReport,
        props.judgementOnMerits,
      ),
    [props.judgementAdd, props.expertReport, props.judgementOnMerits],
  );

  const title = useMemo(() => {
    return hasJom && hasExpertise
      ? translate(messages.courtManagerValidationTitle, props, {
          type: translate(messages.have, props),
          judiciaryStep: `${translate(
            messages.expertReport,
            props,
          )} et le ${translate(messages.jom, props)}`,
          plural: 's',
        })
      : hasJom && !hasExpertise
      ? translate(messages.courtManagerValidationTitle, props, {
          type: translate(messages.has, props),
          judiciaryStep: translate(messages.jom, props),
          plural: '',
        })
      : !hasJom && hasExpertise
      ? translate(messages.courtManagerValidationTitle, props, {
          type: translate(messages.has, props),
          judiciaryStep: translate(messages.expertReport, props),
          plural: '',
        })
      : translate(messages.courtManagerUpdateDialogContent, props);
  }, [props.judgementAdd, props.expertReport, props.judgementOnMerits]);

  return (
    <SahamDialog
      handleValidate={handleValidate}
      handleClose={props.handleClose as MouseEventHandler}
      title={translate(messages.courtManagerConfirmationDialogTitle, props)}
      cancelTitle={translate(
        messages.courtManagerConfirmationDialogCancel,
        props,
      )}
      confirmTitle={translate(
        messages.courtManagerConfirmationDialogConfirm,
        props,
      )}
      open={props.open}
    >
      {featureService.hasFeature(FEATURES.MANAGE_CLAIM_BY_MANAGER) &&
      !isEmpty(
        get(
          props.judgementAdd,
          'judgementAddDocuments',
          [],
        ).filter((item: any) => isEmpty(item.status)),
      )
        ? title
        : featureService.hasFeature(FEATURES.MANAGE_CLAIM_BY_MANAGER) &&
          get(props.judgementAdd, 'status') === PROCEDURE_STATUSES.TO_EDIT
        ? translate(messages.courtManagerConfirmationOnRejectionTitle, props)
        : translate(messages.courtManagerConfirmationDialogContent, props)}
    </SahamDialog>
  );
};

export default injectIntl(ValidationDialog);
